import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Play } from "lucide-react";
import { BRAND, HERO_STATS } from "@/data/site";
import AnimatedCounter from "@/components/AnimatedCounter";

const HERO_VIDEO = "https://cdn.coverr.co/videos/coverr-aerial-view-of-a-cityscape-7053/1080p.mp4";
const HERO_VIDEO_FALLBACK = "https://videos.pexels.com/video-files/2880317/2880317-hd_1920_1080_30fps.mp4";
const HERO_IMG = "https://images.unsplash.com/photo-1529926691761-20fb82067c71?q=85&w=2400";

export default function Hero() {
  const [videoLoaded, setVideoLoaded] = useState(false);

  return (
    <section
      className="relative min-h-[100svh] w-full overflow-hidden bg-navy text-white"
      data-testid="hero-section"
    >
      {/* Background image fallback */}
      <img
        src={HERO_IMG}
        alt=""
        className={`absolute inset-0 w-full h-full object-cover ken-burns transition-opacity duration-700 ${
          videoLoaded ? "opacity-0" : "opacity-90"
        }`}
      />

      {/* Hero video */}
      <video
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        poster={HERO_IMG}
        onLoadedData={() => setVideoLoaded(true)}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
          videoLoaded ? "opacity-100" : "opacity-0"
        }`}
      >
        <source src={HERO_VIDEO_FALLBACK} type="video/mp4" />
        <source src={HERO_VIDEO} type="video/mp4" />
      </video>

      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-navy/85 via-navy/70 to-navy/95" />
      <div className="absolute inset-0 bg-navy/25" />
      <div className="absolute inset-0 hero-grid-overlay opacity-25" />
      <div className="absolute inset-0 grain-overlay" />

      {/* Content */}
      <div className="relative container-x pt-40 pb-32 sm:pt-44 sm:pb-40 min-h-[100svh] flex flex-col justify-between">
        <div className="max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="flex items-center gap-3"
          >
            <span className="w-10 h-px bg-gold" />
            <span className="overline text-gold">India · Est. since 2007</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="font-display font-extrabold text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight mt-6"
          >
            Engineering<br />
            <span className="italic font-light text-gold">Tomorrow.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-6 text-lg sm:text-2xl font-display font-light text-white/85 max-w-2xl"
          >
            Transforming infrastructure, hospitality &amp; retail.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.45 }}
            className="mt-6 text-base text-white/65 max-w-xl leading-relaxed"
          >
            {BRAND.description}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/services"
              data-testid="hero-explore-services-cta"
              className="group inline-flex items-center gap-2 px-7 py-4 bg-gold text-navy font-semibold text-xs tracking-[0.2em] uppercase hover:bg-[#E8C45F] transition-colors"
            >
              Explore Services
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-contact-cta"
              className="inline-flex items-center gap-2 px-7 py-4 border border-white/30 text-white text-xs tracking-[0.2em] uppercase hover:bg-white hover:text-navy transition-colors"
            >
              <Play size={14} /> Contact Us
            </Link>
          </motion.div>
        </div>

        {/* Stats bar */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85 }}
          className="mt-16 glass-dark p-6 sm:p-8 grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-12"
          data-testid="hero-stats-bar"
        >
          {HERO_STATS.map((s, i) => (
            <div key={s.label} className={`${i !== 0 ? "lg:border-l lg:border-white/10 lg:pl-12" : ""}`}>
              <div className="font-display font-extrabold text-4xl sm:text-5xl text-white">
                <AnimatedCounter value={s.value} suffix={s.suffix} />
              </div>
              <div className="mt-2 overline text-white/60">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 right-6 hidden lg:flex items-center gap-3 text-white/60 text-xs tracking-widest uppercase">
        <span>Scroll</span>
        <span className="w-12 h-px bg-white/40" />
      </div>
    </section>
  );
}
